import { Button, Group, Dialog } from '@economic/taco';
import React, { useMemo } from 'react';
import { useField } from 'react-final-form';
import { Field } from '../../../../../shared/components/Form';
import { t } from '../../../../../shared/components/l10n/l10n';
import { DisplayMessages } from '../../shared/components/DisplayMessages';
import { InvoicingMessage, InvoicingMessageType, SendByType } from '../../invoices/book-and-send/types';
import { AutoCloseDrawer } from '../../shared/components/AutoCloseDrawer';
import { useRequiredFieldsValidation } from '../../shared/hooks/useRequiredFieldsValidation';
import { SalesDialogFormWithEmailOptionsDrawer } from '../../shared/components/SalesDialogFormWithEmailOptionsDrawer';
import { SendInvoicesFormData } from '../types';

interface SendInvoicesDialogProps {
    loading: boolean;
    initialData?: SendInvoicesFormData;
    open: boolean;
    onHide: () => void;
    onSend: (values: any) => void;
}

export function SendInvoicesDialog({ loading, initialData, open, onHide, onSend }: SendInvoicesDialogProps) {
    return (
        <SalesDialogFormWithEmailOptionsDrawer
            loading={loading}
            loadingSpinnerWithText
            ariaLabel={t`FORM_ARCHIVESEND_INVOICES_HEADER`}
            open={open}
            className="book-and-send-dialog form h-[500px]"
            size="xs"
            fullEmailDrawerAttachmentsOptions
            onHide={onHide}
            initialData={initialData}
            onSubmit={onSend}
            isBulkSending>
            {(form, drawer) => (
                <>
                    <AutoCloseDrawer drawer={drawer} isBulkSending />
                    <SendInvoicesDialogContent initialData={initialData} onSubmit={() => form.submit()} onClose={onHide} />
                </>
            )}
        </SalesDialogFormWithEmailOptionsDrawer>
    );
}

interface SendInvoicesDialogContentProps {
    initialData?: SendInvoicesFormData;
    onSubmit: () => void;
    onClose: () => void;
}

function SendInvoicesDialogContent({ initialData, onSubmit, onClose }: SendInvoicesDialogContentProps) {
    const requiredFieldValidationMessages = useRequiredFieldsValidation();
    const {
        input: { value: sendByType },
    } = useField('sendByType');

    const messages = useMemo(() => {
        if (!initialData) {
            return [];
        }

        const messages: InvoicingMessage[] = [];

        if (sendByType === SendByType.oioxml && initialData.emailOnly.length > 0) {
            messages.push({
                message: t(`FORM_ARCHIVESEND_CANNOT_SEND_VIA_EMAIL`, {
                    INVOICES: initialData.emailOnly.join(', '),
                }),
                type: InvoicingMessageType.Warning,
            });
        }

        return [...messages, ...requiredFieldValidationMessages];
    }, [initialData, sendByType, requiredFieldValidationMessages]);

    const canSubmit =
        sendByType !== SendByType.undefined && messages.filter(m => m.type === InvoicingMessageType.Error).length === 0;

    return (
        <div className="h-full flex flex-col">
            <Dialog.Title>{t`FORM_ARCHIVESEND_INVOICES_HEADER`}</Dialog.Title>

            <Field.NumberRadioGroup
                name="sendByType"
                options={[
                    {
                        text: t('FORM_ARCHIVESEND_SEND_BY_EMAIL'),
                        value: SendByType.email,
                    },
                    {
                        text: t('FORM_ARCHIVESEND_SEND_BY_EINVOICE'),
                        value: SendByType.oioxml,
                        disabled: !initialData || !initialData.canSendElectronically,
                    },
                ]}
            />
            <div className="book-and-send-dialog__messages">
                <DisplayMessages messages={messages} />
            </div>

            <Dialog.Footer>
                <Group>
                    <Button appearance="discrete" onClick={onClose}>
                        {t('FORM_ARCHIVESEND_BUTTON_CANCEL')}
                    </Button>
                    <Button appearance="primary" type="submit" onClick={onSubmit} disabled={!canSubmit}>
                        {t('FORM_ARCHIVESEND_BUTTON_SEND')}
                    </Button>
                </Group>
            </Dialog.Footer>
        </div>
    );
}
